import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import { useSelector } from "react-redux";
import config from "../../services/config";

const AnatomyPopup = ({ open, handleClose, handleOpen }) => {
  const { category, subcategory } = useSelector(
    (state) => state.GlobalVariables
  );
  const [galleryImages, setGalleryImages] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch images with additional data (ID and other properties)
  const fetchImagebyCategory = async () => {
    setIsLoading(true);
    try {
      const dataToSend = {
        category_id: category,
        group_id: subcategory,
        superCategory: 1,
      };
      const res = await config.getImagebyCategory(dataToSend);

      if (res.status === 200) {
        const images = res.data.flatMap((product) =>
          product.images.map((img) => ({
            id: img.id, // Use image ID
            name: product.item_code, // Product name
            original: img.image,
            thumbnail: img.image,
            originalAlt: img.alt,
            thumbnailAlt: img.alt,
            originalHeight: "400vh",
          }))
        );

        if (images.length > 0) {
          setGalleryImages(images);
        } else {
          setGalleryImages([]); // Handle empty images
        }
      } else {
        setGalleryImages([]);
      }
    } catch (e) {
      console.error(e);
      setGalleryImages([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle image click
  const handleImageClick = async () => {
    if (galleryImages.length === 0) return;

    const clickedImage = galleryImages[selectedImageIndex];

    try {
      handleOpen(clickedImage.name);
    } catch (error) {
      console.error("Error calling API:", error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchImagebyCategory();
    } else {
      setGalleryImages([]);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      fullScreen={window.innerWidth < 600}
      sx={{
        "& .MuiPaper-root": {
          backgroundColor: "#ff7f00", // Orange Theme
          color: "white",
          borderRadius: "12px", // Smooth Borders
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.3)", // Professional Shadow
        },
      }}
    >
      {/* Dialog Title */}
      <DialogTitle
        sx={{
          backgroundColor: "#fcb117", // Darker Orange
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 20px",
        }}
      >
        <Typography variant="h6" fontWeight={"700"}>
          Choose an image to continue:
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: "white",
            backgroundColor: "rgba(255, 0, 0, 0.5)",
            "&:hover": { backgroundColor: "rgba(250, 7, 7, 0.8)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent
        dividers
        sx={{
          padding: "16px",
          overflow: "auto",
          backgroundColor: "#1d2055",
          "& .image-gallery": {
            backgroundColor: "#1d2055",
          },
        }}
      >
        {/* Loading Skeleton */}
        {isLoading ? (
          <Box display="flex">
            <Box
              sx={{
                width: 100,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Skeleton variant="rectangular" width={100} height={80} />
              <Skeleton variant="rectangular" width={100} height={80} />
            </Box>
            <Skeleton
              variant="rectangular"
              width="100%"
              height="60vh"
              sx={{ marginLeft: 2 }}
            />
          </Box>
        ) : galleryImages.length > 0 ? (
          // Image Gallery
          <ImageGallery
            items={galleryImages}
            showThumbnails={true}
            showFullscreenButton={false}
            showPlayButton={false}
            slideDuration={450}
            showNav={true}
            loading={"lazy"}
            thumbnailLoading={"lazy"}
            lazyLoad={true}
            showBullets={true}
            showIndex={true}
            autoPlay={true}
            disableThumbnailScroll={true}
            thumbnailPosition="left"
            onSlide={(index) => setSelectedImageIndex(index)}
            onClick={(event) => handleImageClick(event)}
          />
        ) : (
          // No Images Found
          <Box
            sx={{
              height: "60vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              textAlign: "center",
            }}
          >
            <Typography variant="h5" fontWeight="bold" color="white">
              🚀 NEW FEATURE COMING SOON
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AnatomyPopup;
